import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useRouteError } from 'react-router-dom';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

interface TimeoutError extends Error {
  name: string;
  code?: string;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    // Handle the error state update here instead of componentDidCatch
    if (ErrorBoundary.isTimeoutError(error)) {
      return {
        hasError: true,
        error: new Error('Request timed out. Please check your connection and try again.')
      };
    }
    return { hasError: true, error };
  }

  private static isTimeoutError(error: any): error is TimeoutError {
    return error?.name === 'TimeoutError' || 
           error?.message?.includes('timeout') || 
           error?.code === 'TIMEOUT';
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Only log the error, don't update state here
    console.error('Error caught by boundary:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Oops! Something went wrong</h2>
            <p className="text-gray-600">{this.state.error?.message || 'Please try refreshing the page'}</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

// For use with React Router v6.4+ errorElement
export function ErrorElement() {
  const error = useRouteError() as Error;
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-lg w-full">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Oops! Something went wrong</h2>
        <p className="text-gray-600 mb-4">{error?.message || 'An unexpected error occurred'}</p>
        <div className="flex flex-col space-y-2">
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Refresh Page
          </button>
          <button 
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
}